// ============================================================
// ADMIN CONTROL PANEL — role-based access map
// ============================================================
// Single source of truth for WHICH admin tabs each role may see.
//
// IMPORTANT: this is the UI gate only. It decides what a role is shown,
// not what they are allowed to do. Real authorization lives server-side
// in RLS policies and is_admin()-style RPC guards — the UI must never be
// the only thing standing between a role and an action. This map is
// deliberately aligned to what the server already permits (see the RLS
// SELECT policies for bookings/payments/counter_sales/complaints), so a
// role is never shown a tab whose data the server would deny.
//
// Per-role WRITE permission (who can edit vs only view within a tab) is
// tightened per-domain as each domain is built out; until then, writes
// remain admin-gated by RLS and simply fail-closed for other roles.
// ============================================================

export type AdminTab =
  | 'overview' | 'fleet' | 'live_ops' | 'dispatch' | 'counters' | 'routes' | 'schedules'
  | 'bookings' | 'customers' | 'employees' | 'roster' | 'notices' | 'promotions' | 'counter_sales'
  | 'complaints' | 'payments' | 'finance_oversight' | 'analytics' | 'intelligence' | 'quality'
  | 'incidents' | 'settings';

export type AppRole =
  | 'admin' | 'supervisor' | 'finance' | 'support' | 'counter_staff'
  | 'hr' | 'staff' | 'driver' | 'passenger' | string;

// Ordering here does not matter; the panel renders tabs in its own order,
// filtered to this set.
const TAB_ACCESS: Record<string, AdminTab[]> = {
  admin: [
    'overview', 'fleet', 'live_ops', 'dispatch', 'counters', 'routes', 'schedules',
    'bookings', 'customers', 'employees', 'roster', 'notices', 'promotions', 'counter_sales',
    'complaints', 'payments', 'finance_oversight', 'analytics', 'intelligence', 'quality',
    'incidents', 'settings',
  ],
  // Operational oversight — runs the day, not the money or config.
  supervisor: ['overview', 'fleet', 'live_ops', 'dispatch', 'bookings', 'employees', 'roster', 'quality', 'incidents'],
  // Money-facing — revenue, counter cash, tickets.
  finance: ['overview', 'bookings', 'payments', 'counter_sales', 'finance_oversight', 'analytics', 'intelligence'],
  // Customer care — cases and messaging.
  support: ['overview', 'complaints', 'customers', 'notices', 'bookings', 'incidents'],
  // Counter operations.
  // `counter_sales` was dropped here when the tab became the finance Counter
  // Collections surface. That surface reads `rpc_finance_counter_collections`
  // and `rpc_finance_counter_sales_detail`, both guarded by
  // `is_admin_or_finance()` — a clerk would have been shown a tab that could
  // only ever render an authorization error. A clerk's own sales live on their
  // drawer at `/pos`, which is the surface built for that job.
  counter_staff: ['counters'],
};

/** Tabs a role may see in the admin panel (empty if none). */
export function tabsForRole(role: string | null | undefined): AdminTab[] {
  return TAB_ACCESS[role ?? ''] ?? [];
}

/** True if the role has any admin-panel access at all. */
export function canAccessAdminPanel(role: string | null | undefined): boolean {
  return tabsForRole(role).length > 0;
}

/** True if the role may see a specific tab. */
export function canAccessTab(role: string | null | undefined, tab: AdminTab): boolean {
  return tabsForRole(role).includes(tab);
}

/** Roles allowed through the /admin route guard. */
export const ADMIN_PANEL_ROLES = Object.keys(TAB_ACCESS);

// ── Staff landing paths ─────────────────────────────────────
// Maps each staff role to its "home base" path. Used by the post-login
// redirect hook and the Navbar's "Back to control room" link so the two
// always agree. Returns null for passengers / unknown roles.

const STAFF_LANDING: Record<string, string> = {
  admin:         '/admin',
  supervisor:    '/admin',
  finance:       '/finance/dashboard',
  support:       '/support-staff/dashboard',
  hr:            '/hr',
  // The SmartPOS. A clerk's job is one drawer and one queue; `/counter` is the
  // terminal admin's desk. The legacy `/counter/dashboard` was a second,
  // competing sell path for this same role and was deleted on 2026-07-31.
  counter_staff: '/pos',
  // Same login page as everyone else; the role decides the destination. A
  // counter admin runs a terminal — departures, buses, duty — not a drawer.
  counter_admin: '/counter',
};

/** Landing path for a staff role, or `null` for passengers / unknown. */
export function staffLandingPath(role: string | null | undefined): string | null {
  return STAFF_LANDING[role ?? ''] ?? null;
}

/** True if the role has a staff landing (i.e. is not a passenger). */
export function isStaffRole(role: string | null | undefined): boolean {
  return staffLandingPath(role) !== null;
}
